import { authEndpoints } from "@/api/auth/auth.endpoints";
import type { TValidateSubDomainResponse } from "@/api/auth/auth.mutations.types";
import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { applyPrimaryColorSpectrum } from "@/utils/generateColorSpectrum";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import { parseOriginUrl } from "@/utils/parseOriginUrl";
import { isAxiosError } from "axios";

export type SubdomainBranding = {
  subdomainValid: boolean;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  entityName: string | null;
  theme: string | null;
  isSsoEnabled: boolean;
  providers: { providerName: string }[];
};

const EMPTY_BRANDING: SubdomainBranding = {
  subdomainValid: true,
  logoUrl: null,
  faviconUrl: null,
  primaryColor: null,
  entityName: null,
  theme: null,
  isSsoEnabled: false,
  providers: [],
};

const INVALID_BRANDING: SubdomainBranding = { ...EMPTY_BRANDING, subdomainValid: false };

/**
 * Caches the in-flight (and resolved) request so root and child route loaders
 * share a single network call per full page load.
 */
let inFlight: Promise<SubdomainBranding> | null = null;

/**
 * Clears the cached branding fetch so the next getSubdomainBranding() call
 * hits the network again, e.g. after an entity branding save.
 */
export function resetSubdomainBrandingCache(): void {
  inFlight = null;
}

export function getSubdomainBranding(): Promise<SubdomainBranding> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { subdomain, isValid } = parseOriginUrl(window.location.origin);
    if (!isValid || !subdomain) return EMPTY_BRANDING;

    const subDomainQueryParam = getQueryParamsWithCustomQueryObj({ subDomain: subdomain });

    try {
      const { data } = await axiosInstance.get<TValidateSubDomainResponse>(
        `${authEndpoints.validateSubDomain}?${subDomainQueryParam}`,
        { suppressErrorAlert: true } as CustomAxiosRequestConfig,
      );

      return {
        subdomainValid: true,
        logoUrl: data.data.logoUrl ?? null,
        faviconUrl: data.data.faviconUrl ?? null,
        primaryColor: data.data.primaryColor ?? null,
        entityName: data.data.entityName ?? null,
        theme: data.data.theme ?? null,
        isSsoEnabled: data.data.isSsoEnabled ?? false,
        providers: data.data.providers ?? [],
      };
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return INVALID_BRANDING;
      }
      return INVALID_BRANDING;
    }
  })();

  return inFlight;
}

/**
 * Applies tenant branding side effects (favicon + primary color) to the document.
 * Idempotent — safe to call multiple times.
 */
export function applySubdomainBranding(branding: SubdomainBranding): void {
  if (branding.faviconUrl) {
    const iconLinks = document.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
    );
    iconLinks.forEach((link) => {
      link.href = branding.faviconUrl as string;
      link.removeAttribute("type");
    });
  }

  if (branding.primaryColor) {
    applyPrimaryColorSpectrum(branding.primaryColor);
  }
}
